import { useCallback, useRef, useState } from 'react'
import { Stars } from 'blaze-ui/icons/20'
import { ChevronLeftSmall, ChevronRightSmall } from 'blaze-ui/icons/24'
import Styles from './BeforeAfterSlider.module.scss'

interface Props {
  beforeImage?: string
  afterImage: string
  afterLabel: string
}

export const BeforeAfterSlider = ({ beforeImage, afterImage, afterLabel }: Props) => {
  const [position, setPosition] = useState(50)
  const containerRef = useRef<HTMLDivElement>(null)
  const isDragging = useRef(false)

  const updatePosition = useCallback((clientX: number) => {
    if (!containerRef.current) return
    const rect = containerRef.current.getBoundingClientRect()
    const x = clientX - rect.left
    const pct = Math.min(100, Math.max(0, (x / rect.width) * 100))
    setPosition(pct)
  }, [])

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      isDragging.current = true
      ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
      updatePosition(e.clientX)
    },
    [updatePosition],
  )

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDragging.current) return
      updatePosition(e.clientX)
    },
    [updatePosition],
  )

  const onPointerUp = useCallback(() => {
    isDragging.current = false
  }, [])

  return (
    <div
      ref={containerRef}
      className={Styles.container}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
    >
      <img src={beforeImage} alt="Before" className={Styles.imageBefore} draggable={false} />

      <div className={Styles.afterClip} style={{ clipPath: `inset(0 0 0 ${position}%)` }}>
        <img src={afterImage} alt="After" className={Styles.imageAfter} draggable={false} />
      </div>

      <div className={Styles.divider} style={{ left: `${position}%` }}>
        <div className={Styles.handle}>
          <ChevronLeftSmall size={18} color="var(--dark-90)" />
          <ChevronRightSmall size={18} color="var(--dark-90)" />
        </div>
      </div>

      <div className={Styles.badgeBefore}>Before</div>
      <div className={Styles.badgeStyle}>
        <Stars size={16} color="var(--dark-90)" />
        <span>{afterLabel}</span>
      </div>
      <div className={Styles.badgeAfter}>After</div>
    </div>
  )
}
